import { useState, FormEvent } from "react";

const PLATFORMS = [
  { id: "tiktok", label: "TikTok", icon: "ri-tiktok-line" },
  { id: "youtube", label: "YouTube", icon: "ri-youtube-line" },
  { id: "twitter", label: "Twitter/X", icon: "ri-twitter-x-line" },
  { id: "instagram", label: "Instagram", icon: "ri-instagram-line" },
  { id: "facebook", label: "Facebook", icon: "ri-facebook-line" },
  { id: "other", label: "Other", icon: "ri-global-line" },
];

const CATEGORIES = [
  "Fashion & Style", "Beauty & Skincare", "Tech & Gadgets", "Fitness & Sports",
  "Food & Cooking", "Home & Garden", "Travel & Lifestyle", "Gaming", "Finance", "Other",
];

const FOLLOWER_RANGES = [
  "1K – 10K", "10K – 50K", "50K – 100K", "100K – 500K", "500K – 1M", "1M+",
];

type Step = 1 | 2 | 3;

export default function InfluencerApplicationForm() {
  const [step, setStep] = useState<Step>(1);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [followerRange, setFollowerRange] = useState("");

  const togglePlatform = (id: string) => {
    setSelectedPlatforms((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      data.append(key, value as string);
    });
    data.append("platforms", selectedPlatforms.join(", "));
    data.append("category", selectedCategory);
    data.append("follower_range", followerRange);

    try {
      await fetch("https://readdy.ai/api/form/d7e1ud1noju8nk5a1ig0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = "w-full px-4 py-3 rounded-xl text-sm outline-none transition-all border";
  const inputStyle = { background: "rgba(255,255,255,0.5)", borderColor: "rgba(61,32,0,0.2)", color: "#3D2000" };

  return (
    <section
      id="influencer-apply"
      className="py-16 px-6 md:px-12 relative overflow-hidden"
      style={{ background: "linear-gradient(135deg, #7B5200 0%, #B8860B 30%, #DAA520 60%, #B8860B 100%)" }}
    >
      {/* Shimmer */}
      <div
        className="absolute inset-0 opacity-15"
        style={{ backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.12) 10px, rgba(255,255,255,0.12) 20px)" }}
      />

      <div className="relative z-10 max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <span
            className="inline-block text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-4"
            style={{ background: "rgba(255,255,255,0.2)", color: "#FFD700" }}
          >
            Apply Now
          </span>
          <h2 className="text-4xl font-extrabold mb-3 text-white">
            Join the Influencer Program
          </h2>
          <p className="text-base max-w-xl mx-auto" style={{ color: "rgba(255,255,255,0.8)" }}>
            TikTok stars, YouTube creators, Twitter influencers — apply in 3 minutes and start earning up to 15% commission on every sale.
          </p>
        </div>

        {submitted ? (
          <div
            className="rounded-2xl p-12 text-center border"
            style={{ background: "rgba(255,255,255,0.25)", borderColor: "rgba(255,255,255,0.4)" }}
          >
            <div
              className="w-20 h-20 flex items-center justify-center rounded-full mx-auto mb-6 text-4xl"
              style={{ background: "#FFD700", color: "#3D2000" }}
            >
              <i className="ri-check-double-line" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-3">Application Submitted!</h3>
            <p style={{ color: "rgba(255,255,255,0.8)" }} className="mb-6">
              We review applications within 24–48 hours. You'll receive an email with your approval status and onboarding link.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm" style={{ color: "rgba(255,255,255,0.7)" }}>
              <span className="flex items-center gap-1"><i className="ri-time-line" /> 24–48h review</span>
              <span className="flex items-center gap-1"><i className="ri-mail-line" /> Email confirmation</span>
              <span className="flex items-center gap-1"><i className="ri-gift-line" /> Welcome bonus on approval</span>
            </div>
          </div>
        ) : (
          <>
            {/* Step indicator */}
            <div className="flex items-center justify-center gap-3 mb-8">
              {([1, 2, 3] as Step[]).map((s) => (
                <div key={s} className="flex items-center gap-3">
                  <div
                    className="w-8 h-8 flex items-center justify-center rounded-full text-sm font-bold transition-all"
                    style={
                      step >= s
                        ? { background: "#FFD700", color: "#3D2000" }
                        : { background: "rgba(255,255,255,0.2)", color: "rgba(255,255,255,0.6)" }
                    }
                  >
                    {step > s ? <i className="ri-check-line" /> : s}
                  </div>
                  {s < 3 && (
                    <div
                      className="w-12 h-0.5 rounded-full"
                      style={{ background: step > s ? "#FFD700" : "rgba(255,255,255,0.2)" }}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="text-center text-sm mb-6" style={{ color: "rgba(255,255,255,0.7)" }}>
              {step === 1 && "Step 1 of 3 — Your Info"}
              {step === 2 && "Step 2 of 3 — Social Media"}
              {step === 3 && "Step 3 of 3 — Audience Stats"}
            </div>

            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="rounded-2xl p-8 border"
              style={{ background: "rgba(255,255,255,0.2)", borderColor: "rgba(255,255,255,0.35)" }}
            >
              {/* Step 1 */}
              {step === 1 && (
                <div className="space-y-5">
                  <h3 className="text-lg font-bold text-white mb-4">Personal Information</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold mb-2" style={{ color: "#FFD700" }}>First Name *</label>
                      <input name="first_name" required className={inputClass} style={inputStyle} placeholder="Jessica" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold mb-2" style={{ color: "#FFD700" }}>Last Name *</label>
                      <input name="last_name" required className={inputClass} style={inputStyle} placeholder="Martinez" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold mb-2" style={{ color: "#FFD700" }}>Email Address *</label>
                    <input name="email" type="email" required className={inputClass} style={inputStyle} placeholder="jessica@example.com" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold mb-2" style={{ color: "#FFD700" }}>Phone Number</label>
                    <input name="phone" type="tel" className={inputClass} style={inputStyle} placeholder="+1 (555) 000-0000" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold mb-2" style={{ color: "#FFD700" }}>Country *</label>
                    <input name="country" required className={inputClass} style={inputStyle} placeholder="United States" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold mb-2" style={{ color: "#FFD700" }}>Content Category *</label>
                    <div className="flex flex-wrap gap-2">
                      {CATEGORIES.map((cat) => (
                        <button
                          key={cat}
                          type="button"
                          onClick={() => setSelectedCategory(cat)}
                          className="px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap"
                          style={
                            selectedCategory === cat
                              ? { background: "#FFD700", color: "#3D2000" }
                              : { background: "rgba(255,255,255,0.2)", color: "rgba(255,255,255,0.85)", border: "1px solid rgba(255,255,255,0.3)" }
                          }
                        >
                          {cat}
                        </button>
                      ))}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    disabled={!selectedCategory}
                    className="w-full py-3 rounded-xl font-bold text-sm transition-all cursor-pointer whitespace-nowrap"
                    style={{ background: "#FFD700", color: "#3D2000", opacity: selectedCategory ? 1 : 0.5 }}
                  >
                    Continue to Social Media <i className="ri-arrow-right-line ml-1" />
                  </button>
                </div>
              )}

              {/* Step 2 */}
              {step === 2 && (
                <div className="space-y-5">
                  <h3 className="text-lg font-bold text-white mb-4">Social Media Handles</h3>
                  <div>
                    <label className="block text-xs font-semibold mb-3" style={{ color: "#FFD700" }}>Select Your Platforms *</label>
                    <div className="grid grid-cols-3 gap-3 mb-5">
                      {PLATFORMS.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => togglePlatform(p.id)}
                          className="flex flex-col items-center gap-2 py-3 rounded-xl text-xs font-semibold transition-all cursor-pointer"
                          style={
                            selectedPlatforms.includes(p.id)
                              ? { background: "#FFD700", color: "#3D2000" }
                              : { background: "rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.8)", border: "1px solid rgba(255,255,255,0.25)" }
                          }
                        >
                          <i className={`${p.icon} text-xl`} />
                          {p.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {selectedPlatforms.includes("tiktok") && (
                    <div>
                      <label className="block text-xs font-semibold mb-2" style={{ color: "#FFD700" }}>TikTok Handle</label>
                      <input name="tiktok_handle" className={inputClass} style={inputStyle} placeholder="@yourhandle" />
                    </div>
                  )}
                  {selectedPlatforms.includes("youtube") && (
                    <div>
                      <label className="block text-xs font-semibold mb-2" style={{ color: "#FFD700" }}>YouTube Channel URL</label>
                      <input name="youtube_url" className={inputClass} style={inputStyle} placeholder="youtube.com/c/yourchannel" />
                    </div>
                  )}
                  {selectedPlatforms.includes("twitter") && (
                    <div>
                      <label className="block text-xs font-semibold mb-2" style={{ color: "#FFD700" }}>Twitter/X Handle</label>
                      <input name="twitter_handle" className={inputClass} style={inputStyle} placeholder="@yourhandle" />
                    </div>
                  )}
                  {selectedPlatforms.includes("instagram") && (
                    <div>
                      <label className="block text-xs font-semibold mb-2" style={{ color: "#FFD700" }}>Instagram Handle</label>
                      <input name="instagram_handle" className={inputClass} style={inputStyle} placeholder="@yourhandle" />
                    </div>
                  )}
                  {selectedPlatforms.includes("facebook") && (
                    <div>
                      <label className="block text-xs font-semibold mb-2" style={{ color: "#FFD700" }}>Facebook Page URL</label>
                      <input name="facebook_url" className={inputClass} style={inputStyle} placeholder="facebook.com/yourpage" />
                    </div>
                  )}
                  {selectedPlatforms.includes("other") && (
                    <div>
                      <label className="block text-xs font-semibold mb-2" style={{ color: "#FFD700" }}>Other Platform URL</label>
                      <input name="other_platform_url" className={inputClass} style={inputStyle} placeholder="https://yourplatform.com/profile" />
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-semibold mb-2" style={{ color: "#FFD700" }}>Link to a Recent Post / Video</label>
                    <input name="sample_post_url" className={inputClass} style={inputStyle} placeholder="https://tiktok.com/@you/video/..." />
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      className="flex-1 py-3 rounded-xl font-bold text-sm transition-all cursor-pointer whitespace-nowrap"
                      style={{ background: "rgba(255,255,255,0.2)", color: "white", border: "1px solid rgba(255,255,255,0.3)" }}
                    >
                      <i className="ri-arrow-left-line mr-1" /> Back
                    </button>
                    <button
                      type="button"
                      onClick={() => setStep(3)}
                      disabled={selectedPlatforms.length === 0}
                      className="flex-1 py-3 rounded-xl font-bold text-sm transition-all cursor-pointer whitespace-nowrap"
                      style={{ background: "#FFD700", color: "#3D2000", opacity: selectedPlatforms.length > 0 ? 1 : 0.5 }}
                    >
                      Continue to Audience Stats <i className="ri-arrow-right-line ml-1" />
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3 */}
              {step === 3 && (
                <div className="space-y-5">
                  <h3 className="text-lg font-bold text-white mb-4">Audience &amp; Stats</h3>
                  <div>
                    <label className="block text-xs font-semibold mb-3" style={{ color: "#FFD700" }}>Total Follower Count *</label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {FOLLOWER_RANGES.map((r) => (
                        <button
                          key={r}
                          type="button"
                          onClick={() => setFollowerRange(r)}
                          className="py-2.5 rounded-xl text-xs font-semibold transition-all cursor-pointer whitespace-nowrap"
                          style={
                            followerRange === r
                              ? { background: "#FFD700", color: "#3D2000" }
                              : { background: "rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.8)", border: "1px solid rgba(255,255,255,0.25)" }
                          }
                        >
                          {r}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold mb-2" style={{ color: "#FFD700" }}>Avg. Monthly Views</label>
                      <input name="monthly_views" className={inputClass} style={inputStyle} placeholder="e.g. 500K" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold mb-2" style={{ color: "#FFD700" }}>Avg. Engagement Rate</label>
                      <input name="engagement_rate" className={inputClass} style={inputStyle} placeholder="e.g. 4.5%" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold mb-2" style={{ color: "#FFD700" }}>Primary Audience Location</label>
                    <input name="audience_location" className={inputClass} style={inputStyle} placeholder="e.g. USA, Canada, UK" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold mb-2" style={{ color: "#FFD700" }}>Have you done brand deals before?</label>
                    <select name="brand_deals" className={inputClass} style={inputStyle}>
                      <option value="">Select...</option>
                      <option value="yes_many">Yes, many times</option>
                      <option value="yes_few">Yes, a few times</option>
                      <option value="no">No, this is my first</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold mb-2" style={{ color: "#FFD700" }}>Why do you want to join StarBookPrime? (max 500 chars)</label>
                    <textarea
                      name="motivation"
                      maxLength={500}
                      rows={3}
                      className={`${inputClass} resize-none`}
                      style={inputStyle}
                      placeholder="Tell us about your content and why you'd be a great partner..."
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setStep(2)}
                      className="flex-1 py-3 rounded-xl font-bold text-sm transition-all cursor-pointer whitespace-nowrap"
                      style={{ background: "rgba(255,255,255,0.2)", color: "white", border: "1px solid rgba(255,255,255,0.3)" }}
                    >
                      <i className="ri-arrow-left-line mr-1" /> Back
                    </button>
                    <button
                      type="submit"
                      disabled={submitting || !followerRange}
                      className="flex-1 py-3 rounded-xl font-bold text-sm transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                      style={{ background: "#FFD700", color: "#3D2000", opacity: !followerRange ? 0.5 : 1 }}
                    >
                      {submitting ? (
                        <><i className="ri-loader-4-line animate-spin" /> Submitting...</>
                      ) : (
                        <><i className="ri-send-plane-line" /> Submit Application</>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </form>

            {/* Trust badges */}
            <div className="flex flex-wrap justify-center gap-6 mt-6 text-xs" style={{ color: "rgba(255,255,255,0.65)" }}>
              <span className="flex items-center gap-1"><i className="ri-shield-check-line" /> Free to join</span>
              <span className="flex items-center gap-1"><i className="ri-time-line" /> 24h review</span>
              <span className="flex items-center gap-1"><i className="ri-money-dollar-circle-line" /> Weekly payouts</span>
              <span className="flex items-center gap-1"><i className="ri-lock-line" /> Data protected</span>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
